import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TabsContent } from '@/components/ui/tabs';
import { RefreshCw, Upload } from 'lucide-react';
import { ENTITY_TYPES } from '@/constants/entityTypes';
import { BrokenImageWizard } from './BrokenImageWizard';

const BrandImageField = ({
  label,
  fieldKey,
  value,
  onUpload,
  uploadingBrand,
  helper,
  size = 'wide', // 'wide' | 'logo' | 'pin' | 'favicon'
  accept = 'image/*',
}) => {
  const sizeClass = {
    wide: 'w-full h-32 object-cover',
    logo: 'w-48 h-24 object-contain p-2',
    pin: 'w-24 h-24 object-contain p-2',
    favicon: 'w-16 h-16 object-contain p-1',
  }[size];

  const isImageContain = size !== 'wide';
  const containerClass = isImageContain
    ? `relative ${sizeClass.split(' ').slice(0, 2).join(' ')} border-2 border-border rounded-lg overflow-hidden bg-white dark:bg-gray-100 flex items-center justify-center ${sizeClass.split(' ').slice(2).join(' ')}`
    : `relative ${sizeClass.split(' ').slice(0, 2).join(' ')} border-2 border-border rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-800`;

  const imgClass = isImageContain ? 'max-w-full max-h-full object-contain' : 'w-full h-full object-cover';

  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      {value && (
        <div className={containerClass}>
          <img src={value} alt={label} className={imgClass} />
          {!isImageContain && (
            <div className="absolute bottom-1 right-1 bg-black/50 text-white text-xs px-2 py-1 rounded">
              Preview
            </div>
          )}
        </div>
      )}
      <Input
        type="file"
        accept={accept}
        onChange={(e) => e.target.files[0] && onUpload(e.target.files[0], fieldKey)}
        disabled={uploadingBrand}
      />
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
    </div>
  );
};

const PIN_ACCEPT = 'image/svg+xml,image/png,image/jpeg,image/webp';

const HERO_FIELDS = [
  { key: 'hero_image', label: 'Landing Hero Image', helper: 'Main landing page hero image' },
  { key: 'dashboard_hero', label: 'Dashboard Hero Image', helper: 'Dashboard background image' },
  { key: 'login_background', label: 'Login Background', helper: 'Login page background' },
  { key: 'about_image', label: 'About Section Image', helper: 'About/features section image' },
];

const PIN_FIELDS = [
  {
    key: 'map_pin_default',
    label: 'Default Pin',
    helper: 'Fallback marker shown when a partner type has no custom pin',
  },
  ...ENTITY_TYPES.map((t) => ({
    key: `map_pin_${t.value}`,
    label: `${t.label} Pin`,
    helper: `Marker for ${t.label}`,
  })),
];

export const BrandSettingsTab = ({
  themeSettings,
  uploadingBrand,
  onUploadImage,
  onResetTheme,
  onSaveTheme,
  token,
}) => {
  return (
    <TabsContent value="brand" className="space-y-6">
      <BrokenImageWizard token={token} />
      <div className="rounded-xl border border-border p-6 space-y-8">
        <div>
          <h3 className="text-lg font-semibold mb-4">Logo & Branding</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BrandImageField
              label="Main Logo"
              fieldKey="main_logo"
              value={themeSettings.main_logo}
              onUpload={onUploadImage}
              uploadingBrand={uploadingBrand}
              helper="Recommended: PNG with transparent background"
              size="logo"
            />
            <BrandImageField
              label="Favicon"
              fieldKey="favicon"
              value={themeSettings.favicon}
              onUpload={onUploadImage}
              uploadingBrand={uploadingBrand}
              helper="Recommended: 32x32 or 64x64 PNG/ICO"
              size="favicon"
            />
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Hero & Background Images</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {HERO_FIELDS.map((f) => (
              <BrandImageField
                key={f.key}
                label={f.label}
                fieldKey={f.key}
                value={themeSettings[f.key]}
                onUpload={onUploadImage}
                uploadingBrand={uploadingBrand}
                helper={f.helper}
              />
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Default Images</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <BrandImageField
              label="Default Partner Image"
              fieldKey="default_entity_image"
              value={themeSettings.default_entity_image}
              onUpload={onUploadImage}
              uploadingBrand={uploadingBrand}
              helper="Fallback image for entities without images"
            />
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">Custom Map Pins</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Upload custom location markers for the map. Supported formats: SVG, PNG, JPG, WEBP
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PIN_FIELDS.map((f) => (
              <BrandImageField
                key={f.key}
                label={f.label}
                fieldKey={f.key}
                value={themeSettings[f.key]}
                onUpload={onUploadImage}
                uploadingBrand={uploadingBrand}
                helper={f.helper}
                size="pin"
                accept={PIN_ACCEPT}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onResetTheme}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={onSaveTheme} disabled={uploadingBrand}>
            <Upload className="h-4 w-4 mr-2" />
            Save Brand Settings
          </Button>
        </div>
      </div>
    </TabsContent>
  );
};

export default BrandSettingsTab;
